import { Map } from 'immutable';
import getIn from 'lodash/get';
import React from 'react';
import { call, put, race, select, take } from 'redux-saga/effects';

import * as AppActions from '../../../../src/actions';
import * as UseCaseActions from '../../../actions/UseCases';
import * as AppConstants from '../../../constants';
import { getInDomain } from '../../../domains/externalUtils';
import * as ListenerUtils from '../../../lib/ListenerUtils';
import { getPandoraIdFromMusicId } from '../../../lib/PandoraIdUtil';
import { putAndWait } from '../../../sagas/TaskManager';
import { getStore } from '../../../store/StoreAccessor';
import Logger from '../../../tools/Logger';

const log = Logger.child({
  component: 'mediator/useCases/changeInteractiveRadioMode',
});

export function* changeInteractiveRadioMode({ payload }) {
  const { stationId, modeId, currentModeId, currentItemIndex, modePandoraId } = payload;
  const store = getStore();
  const mode = yield select(
    getInDomain('playback.utils.getInteractiveRadioModeById', Map()),
    modeId,
  );
  let isPremium = yield select(ListenerUtils.hasPremiumFeatures);
  // Right now only used for Artist Only mode to trigger a Premium Access modal coachmark.
  if (mode.get('isPremiumOnly') && !isPremium) {
    const initialSeed = yield select(
      getInDomain('playback.utils.getInitialSeedByStationId'),
      stationId,
    );
    const artistPandoraId = getPandoraIdFromMusicId(initialSeed);
    yield call(
      putAndWait,
      AppActions.UseCases.initiatePremiumAccess({
        targetId: artistPandoraId,
        context: AppConstants.Ads.Rewards.ContextTypes.ARTIST_ONLY,
      }),
    );

    isPremium = yield select(ListenerUtils.hasPremiumFeatures);
    const stillHasStation = yield select(getInDomain('playback.utils.isOwnStation'), stationId); // could have signed in as a different user during PA
    if (!isPremium || !stillHasStation) {
      return;
    }
  }

  try {
    yield call(
      putAndWait,
      getInDomain('playback.actions.setInteractiveRadioMode')({
        stationId,
        modeId,
        previousModeId: currentModeId,
      }),
    );
    const isAPSEnabled = yield select(ListenerUtils.isAPSPandoraStationEnabled);
    const sourceId = isAPSEnabled
      ? yield select(getInDomain('playback.selectors.getCurrentItemSourceId'))
      : stationId;
    yield put(
      UseCaseActions.skipTrackForward({
        forceNewPlaylist: true,
        index: currentItemIndex,
        modePandoraId,
        skipType: AppConstants.Playback.FragmentRequestReasons.CHANGE_MODE,
        sourceId,
        type: AppConstants.SourceTypes.SourceTypes.STATION,
      }),
    );

    const { coachmarkAction } = yield race({
      playTrackAction: take(AppConstants.Playback.ActionTypes.REQUEST_PLAY_TRACK), // skip completed
      apsEventStarted: take(AppConstants.AudioPlayer.AudioPlayerActions.PLAYING), // mode change completed
      coachmarkAction: take(AppConstants.Ads.Rewards.ActionTypes.GLOBAL_CLOSE_REWARDS_COACHMARK), // not enough rewards for skip
    });

    if (
      coachmarkAction &&
      getIn(coachmarkAction, 'data.reason') ===
        AppConstants.External.CoachmarkDismissReasons.ACCEPT_INVITATION_COMPLETE
    ) {
      yield take(AppConstants.Playback.ActionTypes.REQUEST_PLAY_TRACK); // music interrupted, wait for new track or resuming current
    }

    const nowPlayingTrack = yield select(
      getInDomain('playback.utils.getPlaylistCurrentTrack'),
      sourceId,
    );
    const isVoiceTrack = yield select(
      getInDomain('playback.utils.getAttributeForTrackFromActivePlaylist', false),
      nowPlayingTrack,
      'isVoiceTrack',
    );
    const playingModeId = isAPSEnabled
      ? nowPlayingTrack.get('mode').modeId
      : nowPlayingTrack.get('modeId');

    const isModeChanged = isAPSEnabled ? modePandoraId !== playingModeId : modeId !== playingModeId;
    const isAudioAd = yield select(getInDomain('ads.utils.isAudioAd', false), nowPlayingTrack);

    if (!isAudioAd && !isVoiceTrack && isModeChanged) {
      // opted out of reward flow, revert mode
      yield call(
        putAndWait,
        getInDomain('playback.actions.setInteractiveRadioMode')({
          stationId,
          modeId: playingModeId,
          previousModeId: modeId,
        }),
      );
    } else {
      yield put(
        AppActions.Modal.openModal({
          modalType: 'ModalCoachMark',
          body: (
            <div>
              <div>
                <strong>{mode.get('modeName')}</strong>
              </div>
              {mode.get('modeDescription')}
            </div>
          ),
          onClose: () => {
            store.dispatch(AppActions.Modal.closeModal());
          },
        }),
      );
    }
  } catch (error) {
    log.error(
      'Cannot set Interactive Radio mode with stationId and modeId:',
      stationId,
      modeId,
      JSON.stringify(error),
    );
  }
}
